import { useEffect } from 'react';
import {
  ComponentPropsContext,
  SitecoreContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { GetStaticPaths } from 'next';
import { GetStaticPropsContext } from 'next/dist/types';
import { useRouter } from 'next/router';

import { fixMultisiteContext, STORE_SITE_NAME } from 'common/pages/multisite-utils';
import { JabraPageProps } from 'common/pages/pagesTypes';
import { getSharedStaticProps } from 'common/pages/shared-static-props';
import { sitemapFetcher } from 'lib/sitemap-fetcher';
import { componentBuilder } from 'temp/componentBuilder';

import Layout from '../../Layout';

const JabraSitecorePage = ({
  componentProps,
  layoutData,
  headLinks,
  publicUrl,
  appSettings,
  filteredSelectedSites,
}: JabraPageProps): React.JSX.Element => {
  const router = useRouter();

  useEffect(() => {
    // Only refresh editor fast refresh in development mode
    if (process.env.NODE_ENV === 'development') {
      handleEditorFastRefresh();
    }

    if (!layoutData.sitecore.route) {
      router.replace('/');
    }
  }, [layoutData.sitecore.route, router]);

  {
    /*
          Sitecore Pages supports component rendering to avoid refreshing the entire page during component editing.
          If you are using Experience Editor only, this logic can be removed, Layout can be left.
        */
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout
          layoutData={layoutData}
          headLinks={headLinks}
          publicUrl={publicUrl}
          appSettings={appSettings}
          filteredSelectedSites={filteredSelectedSites}
        />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  if (process.env.NODE_ENV !== 'development' && !process.env.DISABLE_SSG_FETCH) {
    try {
      // Note: Next.js runs export in production mode
      paths = await sitemapFetcher.fetch(context);
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

export const getStaticProps = (context: GetStaticPropsContext) => {
  fixMultisiteContext(context, STORE_SITE_NAME);
  return getSharedStaticProps(context);
};

export default JabraSitecorePage;
